/*
  pzz-shipping-bar.js - free delivery progress bar + dispatch / delivery date line (<pzz-free-shipping>).
  Moved out of snippets/pzz-shipping-bar.liquid on 2026-10-05 and loaded ONCE on every page from
  snippets/pzz-load (head, defer).

  Why: the cart drawer only renders the bar while the cart has items. On a page without its own
  bar (anything but a product page), adding the first item made Horizon morph the bar into the
  drawer, but scripts inserted by a morph never run, so <pzz-free-shipping> was never defined and
  the drawer kept the static "FREE Delivery on orders over £34.99" text. Loaded globally, the
  element is always defined and any bar that appears later upgrades itself.
*/
(function () {
  'use strict';

  // Define once; safe even though this snippet is rendered on multiple
  // pages/sections. Each <pzz-free-shipping> instance manages itself.
  if (window.customElements.get('pzz-free-shipping')) return;

  const freeDeliveryThreshold = 34.99;
  const cutoffHour = 15;
  const freeDeliveryPeriod = { start: '2026-08-02', end: '2026-08-31' };

  const exclusionDates = [
    { start: '2024-12-24', end: '2024-12-30' },
    { start: '2024-12-31', end: '2025-01-02' },
    { start: '2025-12-24', end: '2025-12-26' },
    { start: '2025-12-31', end: '2026-01-01' }
  ];

  // ── Shared holiday cache (one fetch per TTL window, shared across every
  //    page load via localStorage — <pzz-free-shipping> lives in the cart
  //    drawer, which theme.liquid mounts on every page, so without this the
  //    gov.uk API was being hit on essentially every single pageview for
  //    data that changes a handful of times a year) ───────────────────────

  const HOLIDAYS_CACHE_KEY = 'pzz_uk_holidays_v1';
  const HOLIDAYS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  let ukHolidays = [];
  let holidaysPromise = null;

  function readHolidaysCache() {
    try {
      const raw = localStorage.getItem(HOLIDAYS_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.dates) || typeof parsed.fetchedAt !== 'number') return null;
      if (Date.now() - parsed.fetchedAt > HOLIDAYS_TTL_MS) return null;
      return parsed.dates;
    } catch (e) {
      return null;
    }
  }

  function writeHolidaysCache(dates) {
    try {
      localStorage.setItem(HOLIDAYS_CACHE_KEY, JSON.stringify({ dates, fetchedAt: Date.now() }));
    } catch (e) {
      // Storage full/unavailable (private mode, etc.) — fine, just re-fetch next time.
    }
  }

  function fetchUKHolidays() {
    if (holidaysPromise) return holidaysPromise;

    const cached = readHolidaysCache();
    if (cached) {
      const today = new Date().toISOString().slice(0, 10);
      ukHolidays = cached.filter(d => d >= today);
      holidaysPromise = Promise.resolve();
      return holidaysPromise;
    }

    holidaysPromise = fetch('https://www.gov.uk/bank-holidays.json')
      .then(r => r.json())
      .then(data => {
        const allDates = data['england-and-wales'].events.map(e => e.date);
        writeHolidaysCache(allDates);
        const today = new Date().toISOString().slice(0, 10);
        ukHolidays = allDates.filter(d => d >= today);
      })
      .catch(err => console.error('PZZ shipping: error fetching UK holidays', err));
    return holidaysPromise;
  }

  // ── Date helpers ─────────────────────────────────────────────────────────

  const toDateString = d => d.toISOString().slice(0, 10);

  function isExcludedOrHoliday(date) {
    const s = toDateString(date);
    const excluded = exclusionDates.some(r =>
      r.end ? s >= r.start && s <= r.end : s === r.start
    );
    return excluded || ukHolidays.includes(s);
  }

  const isValidDispatchDay = d => d.getDay() >= 1 && d.getDay() <= 5 && !isExcludedOrHoliday(d);
  const isValidDeliveryDay = d => d.getDay() !== 0 && !isExcludedOrHoliday(d);

  function nextValid(fromDate, test) {
    const d = new Date(fromDate);
    d.setHours(0, 0, 0, 0);
    while (!test(d)) d.setDate(d.getDate() + 1);
    return d;
  }

  function calculateDeliveryDate() {
    const now = new Date();
    const dow = now.getDay();
    const cutoff = new Date(now);
    cutoff.setHours(cutoffHour, 0, 0, 0);

    let dispatch = new Date(now);
    dispatch.setHours(0, 0, 0, 0);

    if (dow === 0) dispatch.setDate(dispatch.getDate() + 1);
    else if (dow === 6) dispatch.setDate(dispatch.getDate() + 2);
    else if (now >= cutoff) dispatch.setDate(dispatch.getDate() + 1);

    dispatch = nextValid(dispatch, isValidDispatchDay);

    let delivery = new Date(dispatch);
    delivery.setDate(delivery.getDate() + 1);
    return nextValid(delivery, isValidDeliveryDay);
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long'
    }).replace(/(\w+)\s(\d+)/, '$1, $2');
  }

  function getTimeUntilCutoff() {
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(cutoffHour, 0, 0, 0);

    if (now >= cutoff || now.getDay() === 0 || now.getDay() === 6) {
      cutoff.setDate(cutoff.getDate() + 1);
      while (cutoff.getDay() === 0 || cutoff.getDay() === 6) cutoff.setDate(cutoff.getDate() + 1);
      while (isExcludedOrHoliday(cutoff)) {
        cutoff.setDate(cutoff.getDate() + 1);
        while (cutoff.getDay() === 0 || cutoff.getDay() === 6) cutoff.setDate(cutoff.getDate() + 1);
      }
    }

    const diffMs = cutoff - now;
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);

    if (h === 0) return `${m} mins`;
    if (h >= 24) {
      const days = Math.floor(h / 24);
      const rem = h % 24;
      return rem === 0
        ? `${days} day${days > 1 ? 's' : ''}`
        : `${days} day${days > 1 ? 's' : ''} ${rem} hrs`;
    }
    return `${h} hrs ${m} mins`;
  }

  function isWithinFreeDeliveryPeriod() {
    const now = new Date();
    return now >= new Date(freeDeliveryPeriod.start) && now <= new Date(freeDeliveryPeriod.end);
  }

  // ── Shared cart fetch (deduped so several instances share one request) ──

  let cartPromise = null;
  function fetchCartData() {
    if (!cartPromise) {
      cartPromise = fetch('/cart.js')
        .then(r => r.json())
        .catch(err => console.error('PZZ shipping: cart fetch error', err))
        .finally(() => { cartPromise = null; });
    }
    return cartPromise;
  }

  // ── The element ──────────────────────────────────────────────────────────

  class PzzFreeShipping extends HTMLElement {
    connectedCallback() {
      // Fires on initial load, theme editor reloads, and any re-render path
      // that actually re-inserts the element.
      this.refresh();

      // Self-heal after Horizon morphs: a Section Rendering API morph resets
      // our children to the server-rendered (empty) markup WITHOUT
      // disconnecting the element, so connectedCallback never re-fires.
      // Watch for that wipe and rebuild.
      if (!this.__observer) {
        this.__observer = new MutationObserver(() => {
          // Our own refresh() writes content, so this only triggers work when
          // the morph has emptied the date line — no infinite loop.
          const dateEl = this.querySelector('.delivery-date-message');
          if (this.__populated && dateEl && dateEl.innerHTML === '') {
            requestAnimationFrame(() => this.refresh());
          }
        });
        this.__observer.observe(this, { childList: true, subtree: true, attributes: true });
      }
    }

    disconnectedCallback() {
      if (this.__observer) {
        this.__observer.disconnect();
        this.__observer = null;
      }
    }

    get productPrice() {
      const p = this.dataset.productPrice;
      return p ? parseFloat(p) : null;
    }

    setWidth(w) {
      if (this.__filled) {
        this.style.setProperty('--progress', w);
      } else {
        this.__filled = true;
        // First fill waits 0.5s so the width transition runs after the page
        // has settled (cart fetch, holidays, Horizon hydration).
        setTimeout(() => this.style.setProperty('--progress', w), 500);
      }
    }

    updateMessage(cart) {
      const messageEl = this.querySelector('.shipping-message');
      if (!messageEl) return;

      if (isWithinFreeDeliveryPeriod()) {
        messageEl.textContent = 'FREE Delivery all August';
        this.setWidth('100%');
        return;
      }

      // 2026-10-05: a cart that already reaches the threshold wins over the product message,
      // so after adding a qualifying product the bar says "You qualify" instead of staying on
      // "This product qualifies".
      if (cart && cart.item_count > 0 && cart.total_price / 100 >= freeDeliveryThreshold) {
        messageEl.textContent = 'You qualify for free delivery!';
        this.setWidth('100%');
        return;
      }

      if (this.productPrice !== null && this.productPrice >= freeDeliveryThreshold) {
        messageEl.textContent = 'This product qualifies for FREE delivery!';
        this.setWidth('100%');
        return;
      }

      if (!cart) return;

      const totalPrice = cart.total_price / 100;
      const percentage = Math.min((totalPrice / freeDeliveryThreshold) * 100, 100);

      if (cart.item_count === 0) {
        messageEl.textContent = 'FREE Delivery on orders over \u00A334.99';
        this.setWidth('0%');
      } else if (totalPrice >= freeDeliveryThreshold) {
        messageEl.textContent = 'You qualify for free delivery!';
        this.setWidth('100%');
      } else {
        const remaining = (freeDeliveryThreshold - totalPrice).toFixed(2);
        messageEl.textContent = `Add \u00A3${remaining} more to get free delivery.`;
        this.setWidth(percentage + '%');
      }
    }

    async showDeliveryDate() {
      await fetchUKHolidays();
      const el = this.querySelector('.delivery-date-message');
      if (!el || !this.isConnected) return;
      el.innerHTML = `Order in the next <strong>${getTimeUntilCutoff()}</strong> for delivery as early as <strong>${formatDate(calculateDeliveryDate())}</strong>`;
      this.__populated = true;
    }

    refresh() {
      this.showDeliveryDate();
      // Product message straight away when the product qualifies, then always check the cart
      // (it may already qualify: see updateMessage).
      if (this.productPrice !== null && this.productPrice >= freeDeliveryThreshold) {
        this.updateMessage(null);
      }
      fetchCartData().then(cart => this.updateMessage(cart));
    }
  }

  customElements.define('pzz-free-shipping', PzzFreeShipping);

  // ── Global refresh plumbing (registered once) ────────────────────────────

  function refreshAll() {
    document.querySelectorAll('pzz-free-shipping').forEach(el => el.refresh());
  }
  window.pzzRefreshShipping = refreshAll;

  document.addEventListener('cart:updated', refreshAll);
  document.addEventListener('pzz:section:refreshed', refreshAll);

  // Intercept Horizon's fetch-based cart mutations as a fallback signal.
  if (!window.__pzzFetchPatched) {
    window.__pzzFetchPatched = true;
    const _fetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url ?? '');
      if (/\/cart\/(add|update|change)/.test(url)) {
        return _fetch.apply(this, args).then(response => {
          if (response.ok) document.dispatchEvent(new Event('cart:updated'));
          return response;
        });
      }
      return _fetch.apply(this, args);
    };
  }
})();

/* Horizon cart ThemeEvent hook (was a module script in the snippet). Guarded so it registers once.
   The main refresh signal is 'shopify:cart:lines-update' in pzz-load, plus the fetch patch above. */
(function () {
  if (window.__pzzCartUpdateHooked) return;
  window.__pzzCartUpdateHooked = true;
  import('@theme/events')
    .then(function (m) {
      var name = m && m.ThemeEvents && m.ThemeEvents.cartUpdate;
      if (!name) return;
      document.addEventListener(name, function () {
        requestAnimationFrame(function () { if (window.pzzRefreshShipping) window.pzzRefreshShipping(); });
      });
    })
    .catch(function () {
      document.addEventListener('cart:update', function () {
        requestAnimationFrame(function () { if (window.pzzRefreshShipping) window.pzzRefreshShipping(); });
      });
    });
})();
